import { computed, defineComponent, h, ref } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { BlockingStatus } from "@scrile/api-provider/dist/api/BlockingStatusProvider";
import { InputMessageThreadSearch, MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import { useGroups } from "@/hooks/useGroups";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import { useMessageThreadsUpdate } from "@/views/PageMessageThreads/hooks/useMessageThreadsUpdate";
import MessageThreadsView from "./components/MessageThreadsView.vue";
import { getMessageThreadsSortData, sortImplementations } from "./config";
import { useMessageSetting } from "./hooks/useMessageSetting";

export default defineComponent({
  name: "PageMessageThreads",
  components: { MessageThreadsView },
  setup(_, { root }) {
    const { authorities, authUserStrict } = useAuthUser();
    const { hasGroupWithMoreThanOneMember: allowMassMessage, getMyGroups } = useGroups();
    const { userMessageSetting, getUserMessageSetting, saveUserMessageSetting } = useMessageSetting();

    const loading = ref(false);
    const filterLoading = ref(false);
    const term = ref("");
    const blockingStatus = ref<BlockingStatus[]>([]);

    const sortItemsArray = computed(() => getMessageThreadsSortData().sortItems);
    const sortData = computed(() => ({
      ...sortItemsArray.value[userMessageSetting.value?.sort || 0],
    }));

    const getBlockingStatusList = async (messageThreads: MessageThread[]) => {
      if (messageThreads.length === 0 || !messageThreads) {
        return;
      }

      const opponentIds = messageThreads.map(thread =>
        thread.userIds[0] === authUserStrict.value.id ? thread.userIds[1] : thread.userIds[0]
      );
      const result = await providers.BlockingStatusProvider.searchByOpponents({ data: { opponentIds } });
      blockingStatus.value = blockingStatus.value.concat(result);
    };

    const getMessageThreads = (page: number = 0, requestData: InputMessageThreadSearch = {}) => {
      return providers.MessageThreadsProvider.find({
        data: {
          userId: authUserStrict.value.id,
          includeDeleted: false,
          term: term.value || undefined,
          page,
          ...sortData.value,
          ...requestData,
        },
      });
    };

    const { items, data, enableScroll, getFirstPage, getNextPage, getLostItems } = usePager<
      MessageThread,
      InputMessageThreadSearch
    >(getMessageThreads, newItems => getBlockingStatusList(newItems));

    const getGroups = () => {
      if (checkAuthorities(authorities.value, ["updateMyContentPrice"])) return getMyGroups();
    };

    const getPageData = async () => {
      try {
        loading.value = true;
        getUserMessageSetting();
        await Promise.all([getFirstPage(), getGroups()]);
      } finally {
        loading.value = false;
      }
    };

    const reGetFirstPage = async () => {
      try {
        filterLoading.value = true;
        await getFirstPage();
      } finally {
        filterLoading.value = false;
      }
    };

    const getUpdatedThreads = async (ids: string[]) => {
      if (!ids.length || !data.value) return;
      const uniqIds = new Set(ids);
      if (uniqIds.size < data.value.result.length) {
        const response = await getMessageThreads(0, { ids: Array.from(uniqIds), size: uniqIds.size });

        let noUpdatedItems = data.value.result.filter(i => !uniqIds.has(i.id));
        let result = response.result.concat(noUpdatedItems);
        const fn = sortImplementations[sortData.value.sort || ""];
        data.value.result = fn ? result.sort(fn) : result;

        if (data.value.hasNextPage) {
          const extraItemsCount = data.value.result.length - (data.value.page + 1) * data.value.size;
          if (extraItemsCount > 0) {
            data.value.result.splice(-extraItemsCount, extraItemsCount);
          } else {
            await getLostItems(-extraItemsCount);
          }
        }
      } else {
        await reGetFirstPage();
      }
    };

    const { threadUpdating } = useMessageThreadsUpdate(getUpdatedThreads);

    const onGetNextPage = async (cb?: Function) => {
      await threadUpdating.value;
      getNextPage(cb);
    };

    const filterByTerm = debounce(reGetFirstPage, 500);

    const setTerm = async (value: string) => {
      term.value = value;
      filterByTerm();
    };

    const onSort = (select: number) => {
      saveUserMessageSetting({ sort: select });
      reGetFirstPage();
    };

    getPageData();

    return () =>
      h(MessageThreadsView, {
        props: {
          loading: loading.value,
          authUserId: authUserStrict.value.id,
          allowMassMessage: allowMassMessage.value,
          messageThreadList: items.value,
          blockingStatus: blockingStatus.value,
          scroll: enableScroll.value,
          currentSortItem: userMessageSetting.value?.sort || 0,
          filterLoading: filterLoading.value,
          term: term.value,
        },
        on: {
          sort: onSort,
          massMessage: () => root.$router.push({ name: "massMessage" }),
          scheduledMassMessage: () => root.$router.push({ name: "scheduledMassMessages" }),
          next: onGetNextPage,
          filter: setTerm,
        },
      });
  },
});
